import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  flexRender,
  createColumnHelper,
  Table as TableType,
} from '@tanstack/react-table';

// Types
import {
  TableProps,
  RoutePresetRow,
  SearchbarFilterTypes,
  routePresetsColumnNames,
} from './types';

// Styled components
import {
  ContainerTable,
  ActivePresetsTableContainer,
  InactivePresetsTableContainer,
  FiltersContainer,
  TrStyled,
  SwapPresetButtonRight,
  SwapPresetButtonLeft,
  ActionCell,
  ActionButton,
  ActionIcon,
} from './styles';

// Compartidos de las tablas de gestión (los mismos que usa Usuarios)
import {
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
} from '../../../summaryTables/SummaryTablesStyles';
import {
  InputRadioContainer,
  InputRadioStyled,
  MultiLineCell,
} from '../../../components/MainStyledComponents';
import { Typography } from '../../../../Typography';
import SearchBar from '../../../../../inputs/SearchBar';

// Icons
import EditIcon from '../../../../../../assets/icons/edit-icon.svg';
import RemoveIcon from '../../../../../../assets/icons/remove-icon.svg';
import { matchesSearch } from '../../../../../../utils/textSearch';

/**
 * Catálogo de plantillas de recorrido del módulo de Gestión.
 *
 * Layout y mecánica calcados de `ExternalCompaniesManagementTable`: dos tablas
 * EN FILA (activas | inactivas), selección por radio y flechas ⮕ / ⬅ debajo de
 * cada una para moverlas de un lado al otro; acciones de fila que abren su
 * modal — la tabla solo las pide, nunca las ejecuta.
 *
 * Columnas: **Nombre** (el que verá el operario en el desplegable del
 * asistente) y **Carretera**, que apila una línea por vía con su rango entre
 * paréntesis — el formato de la vista previa del recorrido. Cuatro columnas en
 * total, las mismas que Empresas externas, porque las dos tablas van en fila y
 * cada una dispone de media pantalla.
 *
 * El borrado NO tiene condición de uso —a diferencia de las empresas externas—
 * porque ningún recorrido apunta a la plantilla: el asistente copia su
 * contenido, no la referencia.
 */
export const RoutePresetsManagementTable = ({
  data,
  onDeactivate,
  onActivate,
  onEditPreset,
  onDeletePreset,
}: TableProps): JSX.Element => {
  const { t } = useTranslation(['pages', 'tables', 'buttons']);

  const [activeData, setActiveData] = useState(data.activePresets);
  const [inactiveData, setInactiveData] = useState(data.inactivePresets);

  useEffect(() => {
    setActiveData(data.activePresets);
    setInactiveData(data.inactivePresets);
  }, [data]);

  const [activeChecked, setActiveChecked] = useState({});
  const [inactiveChecked, setInactiveChecked] = useState({});

  const columnHelper = createColumnHelper<RoutePresetRow>();

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        cell: ({ row }) => (
          <InputRadioContainer>
            <InputRadioStyled
              name="routePresetRadio"
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </InputRadioContainer>
        ),
      }),
      columnHelper.accessor((row) => row.name, {
        id: routePresetsColumnNames.name.id,
        header: () => t(routePresetsColumnNames.name.labelKey),
        cell: (info) => info.getValue(),
      }),
      // 🔴 UNA columna, una línea por carretera, con el rango entre paréntesis:
      // el formato de la vista previa del recorrido. El criterio de la casa es
      // que el rango va pegado a su carretera y nunca suelto — un PK a secas no
      // dice en qué vía está, y los de una troncal y su ramal no son
      // comparables.
      columnHelper.accessor((row) => row.roadsLabel, {
        id: routePresetsColumnNames.roads.id,
        header: () => t(routePresetsColumnNames.roads.labelKey),
        cell: (info) => (
          <MultiLineCell size={'S'}>{info.getValue()}</MultiLineCell>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: () => t('tables:headers.actions'),
        cell: ({ row }) => (
          <ActionCell>
            <ActionButton
              type="button"
              title={t('buttons:edit')}
              onClick={(e) => {
                e.stopPropagation();
                onEditPreset(row.original);
              }}
            >
              <ActionIcon src={EditIcon} alt={t('buttons:edit')} />
            </ActionButton>
            <ActionButton
              type="button"
              title={t('buttons:delete')}
              onClick={(e) => {
                e.stopPropagation();
                onDeletePreset(row.original);
              }}
            >
              <ActionIcon src={RemoveIcon} alt={t('buttons:delete')} />
            </ActionButton>
          </ActionCell>
        ),
      }),
    ],
    [onEditPreset, onDeletePreset, t]
  );

  const activeSearchFilter = useCallback<SearchbarFilterTypes>(
    (value) => {
      setActiveData(
        value === ''
          ? data.activePresets
          : data.activePresets.filter((preset) =>
            matchesSearch([preset.name, preset.roadsLabel], value)
          )
      );
    },
    [data.activePresets]
  );

  const inactiveSearchFilter = useCallback<SearchbarFilterTypes>(
    (value) => {
      setInactiveData(
        value === ''
          ? data.inactivePresets
          : data.inactivePresets.filter((preset) =>
            matchesSearch([preset.name, preset.roadsLabel], value)
          )
      );
    },
    [data.inactivePresets]
  );

  const activeTable = useReactTable({
    getRowId: (row) => row.id,
    data: activeData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setActiveChecked,
    state: { rowSelection: activeChecked },
    enableRowSelection: true,
    enableMultiRowSelection: false,
  });

  const inactiveTable = useReactTable({
    getRowId: (row) => row.id,
    data: inactiveData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setInactiveChecked,
    state: { rowSelection: inactiveChecked },
    enableRowSelection: true,
    enableMultiRowSelection: false,
  });

  const renderTable = (table: TableType<RoutePresetRow>) => (
    <TableContainer>
      <Table>
        <Thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Th key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                </Th>
              ))}
            </tr>
          ))}
        </Thead>
        <Tbody>
          {table.getRowModel().rows.map((row) => (
            <TrStyled
              key={row.id}
              checked={row.getIsSelected()}
              indeterminate={!table.getIsSomeRowsSelected()}
              onClick={() => row.getToggleSelectedHandler()(row)}
            >
              {row.getVisibleCells().map((cell) => (
                <Td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              ))}
            </TrStyled>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );

  const moveSelected = (
    table: TableType<RoutePresetRow>,
    action: (id: string) => Promise<void>
  ) => {
    const selected = table.getSelectedRowModel().rows[0];
    if (!selected) return;
    void action(selected.original.id);
    table.resetRowSelection(true);
  };

  return (
    <ContainerTable>
      <ActivePresetsTableContainer>
        <Typography size="L" variant="semiBold">
          {t('pages:management.activeRoutePresets')} ({activeData.length})
        </Typography>
        <FiltersContainer>
          <SearchBar
            variant="form"
            onChangeInput={(e) => activeSearchFilter(e.target.value)}
          />
        </FiltersContainer>
        {renderTable(activeTable)}
        <SwapPresetButtonRight
          type="button"
          title={t('pages:management.deactivate')}
          disabled={activeTable.getSelectedRowModel().rows.length === 0}
          onClick={() => moveSelected(activeTable, onDeactivate)}
        >
          ⮕
        </SwapPresetButtonRight>
      </ActivePresetsTableContainer>

      <InactivePresetsTableContainer>
        <Typography size="L" variant="semiBold">
          {t('pages:management.inactiveRoutePresets')} ({inactiveData.length})
        </Typography>
        <FiltersContainer>
          <SearchBar
            variant="form"
            onChangeInput={(e) => inactiveSearchFilter(e.target.value)}
          />
        </FiltersContainer>
        {renderTable(inactiveTable)}
        <SwapPresetButtonLeft
          type="button"
          title={t('pages:management.activate')}
          disabled={inactiveTable.getSelectedRowModel().rows.length === 0}
          onClick={() => moveSelected(inactiveTable, onActivate)}
        >
          ⬅
        </SwapPresetButtonLeft>
      </InactivePresetsTableContainer>
    </ContainerTable>
  );
};
